import type { ExtraProductSelectionInput } from '@eti/schema-types';
import type { FormStateMap } from 'redux-form';
import { reduxForm } from 'redux-form';
import type { MapDispatchToProps, MapStateToProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import type { MutableRefObject } from 'react';
import { getDestination } from '../../../../../utils/format';
import type { OrderTypePostbooking } from '../../utils/OrderUtils';
import ExtraProductsPage from '../components/ExtraProductsPage';
import { withPostBookingValidationProvider } from '../components/WithPostBookingValidationProvider';
import getGraphQlSelectionForAllProducts from '../../../../../utils/getGraphQlSelectionForAllProducts';
import { getSimpleVisaInitialValues } from '../../utils/simpleVisaUtils';
import { fillExtraProductsForm } from '../../../../../common/debug/utils';
import { PRODUCTS } from '../../../../../constants/formNamesConstants';

type ConnectedStateProps = {
  getGraphQlSelection: () => ExtraProductSelectionInput[];
};

type DispatchProps = {
  fillForm: () => void;
};

type OwnProps = {
  contactEmail: string | null;
  onAddToAddonCart: (selection: ExtraProductSelectionInput[]) => Promise<void>;
  order: OrderTypePostbooking;
  isAddToAddonCartLoading: boolean;
  tabHeading1Ref?: MutableRefObject<HTMLHeadingElement | null>;
};

interface ReduxFormProps {
  form: string;
  handleSubmit: (arg: () => Promise<void>) => () => void;
  reset: () => void;
}

type ExtraProductsPageContainerTypes = OwnProps &
  ConnectedStateProps &
  DispatchProps &
  ReduxFormProps;

const ExtraProductsPageComponent = ({
  contactEmail,
  fillForm,
  form,
  getGraphQlSelection,
  handleSubmit,
  isAddToAddonCartLoading,
  onAddToAddonCart,
  order,
  reset,
  tabHeading1Ref,
}: ExtraProductsPageContainerTypes) => {
  const { cityCode } = getDestination(order?.bounds);

  const onSubmit = handleSubmit(async () => {
    const graphQlSelection = getGraphQlSelection();
    await onAddToAddonCart(graphQlSelection);
  });

  return (
    <ExtraProductsPage
      contactEmail={contactEmail}
      destinationCityCode={cityCode}
      fillForm={fillForm}
      form={form}
      isLoading={isAddToAddonCartLoading}
      onSubmit={onSubmit}
      order={order}
      reset={reset}
      tabHeading1Ref={tabHeading1Ref}
    />
  );
};

const withReduxForm = reduxForm({
  form: PRODUCTS,
  touchOnChange: true,
});

interface State {
  form: FormStateMap;
}

const mapStateToProps: MapStateToProps<ConnectedStateProps, OwnProps, State> = (
  state,
  { order },
) => {
  return {
    getGraphQlSelection: () => getGraphQlSelectionForAllProducts({ form: 'products', state }),
    initialValues: {
      ...getSimpleVisaInitialValues(order),
    },
  };
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = (dispatch) => ({
  fillForm: () => {
    fillExtraProductsForm(dispatch);
  },
});

export default compose<any>(
  connect(mapStateToProps, mapDispatchToProps),
  withReduxForm,
  withPostBookingValidationProvider,
)(ExtraProductsPageComponent);
